import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DownloadOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  HistoryOutlined,
  RefreshOutlined,
  UndoOutlined,
} from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { CommonDrawerStyle, DrawerHeader } from "../Common/styles/drawer";
import ScreenerImportFailedRowsTable from "./ScreenerImportFailedRowsTable";
import {
  extractFailedRows,
  ScreenerImportChunk,
  ScreenerImportRowResult,
} from "./screenerImportRowResults";

type ImportJob = {
  id: string;
  status: string;
  phase?: string | null;
  originalFilename?: string | null;
  totalRows?: number;
  processedRows?: number;
  successCount?: number;
  failedCount?: number;
  createdAt?: string;
  rolledBackAt?: string | null;
  errorSummary?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onRollbackComplete?: () => void;
};

/** Hidden until rollback is production-ready. */
const SHOW_ROLLBACK = false;

const statusColor = (
  status: string,
): "success" | "error" | "warning" | "info" | "default" => {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "completed_with_errors":
      return "warning";
    case "failed":
      return "error";
    case "running":
    case "pending":
      return "info";
    default:
      return "default";
  }
};

const titleCase = (value?: string | null) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (value?: string) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
};

const unwrap = (response: unknown): unknown => {
  let value = (response as { data?: unknown })?.data ?? response;
  while (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length <= 3 &&
    "data" in value
  ) {
    value = (value as { data: unknown }).data;
  }
  return value;
};

const ScreenerImportHistoryDrawer = ({
  open,
  onClose,
  onRollbackComplete,
}: Props) => {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<ImportJob | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [loadingFailedRowsId, setLoadingFailedRowsId] = useState<string | null>(
    null,
  );
  const [failedRowsByJobId, setFailedRowsByJobId] = useState<
    Record<string, ScreenerImportRowResult[]>
  >({});

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get(
        "/admin/registry/participants/screener-import/jobs?page=1&size=20",
      );
      const payload = res?.data?.data ?? res?.data ?? {};
      const rows = Array.isArray(payload)
        ? payload
        : payload?.data ?? payload?.items ?? payload?.results ?? [];
      setJobs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void loadJobs();
  }, [loadJobs, open]);

  const canRollback = (job: ImportJob) =>
    !["pending", "running", "rolled_back"].includes(
      String(job.status).toLowerCase(),
    );

  const confirmRollback = async () => {
    if (!rollbackTarget) return;
    setRollingBack(true);
    try {
      await http.post(
        `/admin/registry/participants/screener-import/jobs/${rollbackTarget.id}/rollback`,
      );
      toastMessage("success", "Mapped import rolled back.");
      setRollbackTarget(null);
      await loadJobs();
      onRollbackComplete?.();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setRollingBack(false);
    }
  };

  const downloadResult = async (job: ImportJob) => {
    setDownloadingId(job.id);
    try {
      const res = await http.get(
        `/admin/registry/participants/screener-import/jobs/${job.id}/result`,
      );
      const payload = res?.data?.data ?? res?.data ?? {};
      const url = payload?.url ?? payload?.downloadUrl ?? payload?.resultUrl;
      if (!url) {
        toastMessage("warning", "No result file is available for this import.");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setDownloadingId(null);
    }
  };

  const toggleFailedRows = async (job: ImportJob) => {
    if (expandedJobId === job.id) {
      setExpandedJobId(null);
      return;
    }

    setExpandedJobId(job.id);

    if (failedRowsByJobId[job.id]?.length) return;

    setLoadingFailedRowsId(job.id);
    try {
      const res = await http.get(
        `/admin/registry/participants/screener-import/jobs/${job.id}`,
      );
      const payload = unwrap(res) as { chunks?: ScreenerImportChunk[] };
      const failedRows = extractFailedRows(payload.chunks ?? []);
      setFailedRowsByJobId((current) => ({
        ...current,
        [job.id]: failedRows,
      }));
    } catch (err) {
      errorToastMessage(err as Error);
      setExpandedJobId(null);
    } finally {
      setLoadingFailedRowsId(null);
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            ...CommonDrawerStyle,
            width: { xs: "100vw", sm: "680px" },
            maxWidth: "100vw",
            overflowY: "auto",
          }}
        >
          <DrawerHeader title="Mapped Import History" closeDrawer={onClose} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Recent imports and result files
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={loadJobs} disabled={loading}>
                  <RefreshOutlined />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {!loading && jobs.length === 0 && (
            <Paper
              variant="outlined"
              sx={{ p: 5, textAlign: "center", bgcolor: "action.hover" }}
            >
              <HistoryOutlined color="disabled" sx={{ fontSize: 44, mb: 1 }} />
              <Typography fontWeight={600}>No mapped imports yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Completed and in-progress imports will appear here.
              </Typography>
            </Paper>
          )}

          <Stack spacing={1.5}>
            {jobs.map((job) => {
              const total = Number(job.totalRows ?? 0);
              const processed = Number(job.processedRows ?? 0);
              const failedCount = Number(job.failedCount ?? 0);
              const progress = total
                ? Math.min(100, (processed / total) * 100)
                : 0;
              const running = ["pending", "running"].includes(
                String(job.status).toLowerCase(),
              );
              const isExpanded = expandedJobId === job.id;
              const failedRows = failedRowsByJobId[job.id] ?? [];
              const loadingFailedRows = loadingFailedRowsId === job.id;

              return (
                <Paper
                  key={job.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    transition: "border-color 0.2s ease",
                    ...(isExpanded && {
                      borderColor: "warning.light",
                    }),
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>
                          {job.originalFilename || "Mapped import"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(job.createdAt)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={titleCase(job.status)}
                        color={statusColor(job.status)}
                      />
                    </Stack>

                    {running && (
                      <Box>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {titleCase(job.phase) || "Processing"}
                          </Typography>
                          <Typography variant="caption">
                            {processed.toLocaleString()} / {total.toLocaleString()}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant={total ? "determinate" : "indeterminate"}
                          value={progress}
                          sx={{ height: 6, borderRadius: 6 }}
                        />
                      </Box>
                    )}

                    {!running && (
                      <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                        <Typography variant="body2" color="success.main">
                          {Number(job.successCount ?? 0).toLocaleString()} successful
                        </Typography>
                        <Typography
                          variant="body2"
                          color={failedCount ? "error.main" : "text.secondary"}
                        >
                          {failedCount.toLocaleString()} failed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {total.toLocaleString()} total
                        </Typography>
                      </Stack>
                    )}

                    {!running && failedCount > 0 && job.errorSummary && (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        {job.errorSummary}
                      </Alert>
                    )}

                    {!running && failedCount > 0 && (
                      <Box>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          onClick={() => toggleFailedRows(job)}
                          endIcon={
                            loadingFailedRows ? (
                              <CircularProgress size={14} />
                            ) : isExpanded ? (
                              <ExpandLessOutlined />
                            ) : (
                              <ExpandMoreOutlined />
                            )
                          }
                          sx={{
                            px: 0,
                            minWidth: 0,
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          {isExpanded ? "Hide failed rows" : "View failed rows"}
                        </Button>
                        <Collapse in={isExpanded} unmountOnExit>
                          <Box sx={{ mt: 1.5 }}>
                            {loadingFailedRows ? (
                              <Stack alignItems="center" py={2} spacing={1}>
                                <CircularProgress size={22} />
                                <Typography variant="caption" color="text.secondary">
                                  Loading failed rows…
                                </Typography>
                              </Stack>
                            ) : failedRows.length ? (
                              <ScreenerImportFailedRowsTable
                                rows={failedRows}
                                maxHeight={240}
                              />
                            ) : (
                              <Alert severity="info" sx={{ py: 0.5 }}>
                                No failed row details are available. Download the
                                result file for the full report.
                              </Alert>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {!running && failedCount === 0 && job.errorSummary && (
                      <Alert severity="error" sx={{ py: 0 }}>
                        {job.errorSummary}
                      </Alert>
                    )}

                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      {!running && job.status !== "rolled_back" && (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={
                            downloadingId === job.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <DownloadOutlined />
                            )
                          }
                          disabled={downloadingId === job.id}
                          onClick={() => downloadResult(job)}
                        >
                          Result
                        </Button>
                      )}
                      {SHOW_ROLLBACK && (
                      <Tooltip
                        title={
                          canRollback(job)
                            ? "Remove records created by this import"
                            : running
                              ? "Running imports cannot be rolled back"
                              : "This import was already rolled back"
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<UndoOutlined />}
                            disabled={!canRollback(job)}
                            onClick={() => setRollbackTarget(job)}
                          >
                            Rollback
                          </Button>
                        </span>
                      </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Drawer>

      {SHOW_ROLLBACK && (
      <Dialog
        open={!!rollbackTarget}
        onClose={() => !rollingBack && setRollbackTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rollback mapped import?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes participants and screener responses created by{" "}
            <strong>
              {rollbackTarget?.originalFilename || "this import"}
            </strong>
            . Existing participants that were matched during import will remain.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRollbackTarget(null)}
            disabled={rollingBack}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={rollingBack}
            onClick={confirmRollback}
            startIcon={rollingBack ? <CircularProgress size={16} /> : <UndoOutlined />}
          >
            Rollback import
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </>
  );
};

export default ScreenerImportHistoryDrawer;
